import { useLayoutEffect, useRef } from "react";

import type { SessionResultOut } from "../contract/api";
import { t } from "../contract/strings";
import { CalendarIcon, TimerIcon, cx } from "../ui";
import { meetingTitle } from "./meeting-title";
import { CHIP, detailsParts, resultTopics } from "./result-model";

/** Every topic of the meeting, each a wrapping pill of the topic's text. No cap. */
export function TopicChips({ topics }: { topics: string[] }) {
  return (
    <ul aria-label={t("result.topics")} className="flex flex-wrap gap-tight m-0 p-0 list-none">
      {topics.map((topic) => (
        <li key={topic} className={CHIP}>
          {topic}
        </li>
      ))}
    </ul>
  );
}

/**
 * The meeting's details card, above the tabs (#764): the date and the start–end time on
 * the left, the duration in a pill on the right, and the meeting's topics under them.
 *
 * It sticks to the top with the tab bar. `collapsed` is the scrolled-down form: the first
 * row only, the topics folded away (and taken out of the focus order and the
 * accessibility tree). The fold animates briefly, and not at all for a reader who asked
 * for reduced motion.
 *
 * The first row is also where the meeting's name takes over. While the card is pinned at
 * the top of the page the date, the time and the duration step aside for the name, so the
 * one sticky row that is already there becomes the header rather than a second bar being
 * added for it. The two cross-fade in the same cell. Folded, the card also gives up its
 * width and its padding and shrinks to the name (`w-fit`, centred), a small box on the
 * band the screen paints behind it; its colour, its rule and its radius stay the card's --
 * it is a small card, not a second pill.
 */
export function DetailsCard({
  result,
  collapsed,
  onFoldHeight,
}: {
  result: SessionResultOut;
  collapsed: boolean;
  /**
   * The height, in pixels, the topics row gives up when the card folds. The screen keeps
   * that much room at the end of the page while folded, so the page does not get shorter,
   * the browser does not pull the scroll back up and the card does not unfold again.
   */
  onFoldHeight?: (px: number) => void;
}) {
  const { date, time, duration } = detailsParts(result.created_at, result.duration_sec);
  const topics = resultTopics(result);
  const name = meetingTitle(result.title, result.created_at);
  const fold = useRef<HTMLDivElement | null>(null);
  const fade = "transition-opacity duration-200 ease-out motion-reduce:transition-none";

  useLayoutEffect(() => {
    if (!onFoldHeight) return;
    // The clipped content's full height (the gap above the pills included), which the
    // fold's own transition does not change -- measuring the animating row read a
    // part-way height. An unchanged number is a no-op for the caller.
    onFoldHeight(fold.current?.scrollHeight ?? 0);
  });
  return (
    <section
      aria-label={t("result.details")}
      data-collapsed={collapsed ? "true" : "false"}
      className={cx(
        "bg-surface-2 border border-line rounded-card flex flex-col",
        "transition-[padding] duration-200 ease-out motion-reduce:transition-none",
        collapsed ? "mx-auto w-fit max-w-full px-chip-x py-1" : "w-full px-4 py-3",
      )}
    >
      {/* One cell, two rows: the details while the card is in the page, the meeting's name
          once it is pinned. */}
      <div className="grid relative">
        <div
          aria-hidden={collapsed ? true : undefined}
          className={cx(
            "col-start-1 row-start-1 flex items-center justify-between gap-2",
            fade,
            // Folded, the details leave the flow: the card shrinks to the name, not to
            // the date, the time and the pill it is no longer showing.
            collapsed ? "opacity-0 absolute inset-x-0 top-0" : "opacity-100",
          )}
        >
          {/* Mono at label size, without the label register's wide tracking, so the date
              and the time fit one phone-width row beside the pill; it wraps, never cuts. */}
          <p className="m-0 min-w-0 flex flex-wrap items-center gap-x-tight gap-y-nudge font-code font-semibold text-label text-ink-soft tabular-nums">
            {date ? (
              <span className="inline-flex items-center gap-tight whitespace-nowrap">
                <CalendarIcon className="size-icon-xs flex-none" />
                {date}
              </span>
            ) : null}
            {date && time ? <span aria-hidden="true">•</span> : null}
            {time ? <span className="whitespace-nowrap">{time}</span> : null}
          </p>
          <span className="flex-none inline-flex items-center gap-nudge bg-surface border border-line rounded-pill px-chip-x py-1">
            <TimerIcon className="size-icon-xs text-accent" />
            <span className="font-code font-bold text-label text-accent tabular-nums">{duration}</span>
          </span>
        </div>

        <p
          aria-hidden={collapsed ? undefined : true}
          data-details-name
          className={cx(
            "col-start-1 row-start-1 m-0 min-w-0 truncate self-center text-center",
            "font-title font-bold uppercase tracking-name text-label text-ink",
            fade,
            collapsed ? "opacity-100" : "opacity-0",
          )}
        >
          {name}
        </p>
      </div>

      {topics.length ? (
        <div
          data-details-fold
          aria-hidden={collapsed ? true : undefined}
          inert={collapsed}
          className={cx(
            "grid transition-[grid-template-rows,opacity,width] duration-200 ease-out motion-reduce:transition-none",
            collapsed ? "grid-rows-[0fr] opacity-0 w-0" : "grid-rows-[1fr] opacity-100 w-full",
          )}
        >
          <div ref={fold} className="min-h-0 overflow-hidden">
            <div className="pt-stack">
              <TopicChips topics={topics} />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
